/**
 * FRONT-3103: bounded look-ahead for the audio-answer player. Pure and
 * DOM-free so the start order and the memory bound are unit-testable.
 *
 * `get(i)` makes sure chunks i..i+lookahead have been STARTED (each exactly
 * once, in index order) and returns chunk i's promise. `release(i)` drops the
 * stored promise so a played AudioBuffer can be garbage-collected — on a phone
 * the decoded audio of a long answer must never pile up.
 */
export interface ChunkPrefetcher<T> {
  get(index: number): Promise<T>;
  release(index: number): void;
}

export function createChunkPrefetcher<T>(
  count: number,
  lookahead: number,
  load: (index: number) => Promise<T>,
): ChunkPrefetcher<T> {
  const started = new Map<number, Promise<T>>();
  // The next index to start (everything below it has been started) — an index
  // is never started twice, even after it was released.
  let nextToStart = 0;

  function startUpTo(last: number): void {
    while (nextToStart <= last && nextToStart < count) {
      const index = nextToStart++;
      const p = load(index);
      // Nobody may be awaiting this yet (or ever, after a stop): an
      // unobserved rejection would surface as an unhandled rejection.
      p.catch(() => undefined);
      started.set(index, p);
    }
  }

  return {
    get(index: number): Promise<T> {
      startUpTo(index + lookahead);
      const p = started.get(index);
      if (!p) {
        return Promise.reject(new Error(`chunk ${index} is not available (released or out of range)`));
      }
      return p;
    },
    release(index: number): void {
      started.delete(index);
    },
  };
}
